import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Trash } from "@phosphor-icons/react";
import { ArrowLeft, Download } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { toast } from "sonner";
import { MODE_CFG } from "@/components/projects/modeConfig";
import { NewProjectDialog } from "@/components/projects/NewProjectDialog";
import { ProjectsToolbar } from "@/components/projects/ProjectsToolbar";
import { FoldersGrid } from "@/components/projects/FoldersGrid";
import { ProjectsGridView } from "@/components/projects/ProjectsGridView";
import { ProjectsTable } from "@/components/projects/ProjectsTable";
import { MoveFolderDialog } from "@/components/projects/MoveFolderDialog";
import { RenameFolderDialog } from "@/components/projects/RenameFolderDialog";
import { EditSettingsDialog } from "@/components/projects/EditSettingsDialog";
import { ClientAccountDialog } from "@/components/projects/ClientAccountDialog";
import { DeleteProjectDialog, BulkDeleteDialog } from "@/components/projects/DeleteDialogs";
import { TrashView } from "@/components/projects/TrashView";
import { ArchiveView } from "@/components/projects/ArchiveView";
import { OwnersGrid } from "@/components/projects/OwnersGrid";
import { Archive } from "lucide-react";

export default function Projects({ mode = "remote-sensing" }) {
  const cfg = MODE_CFG[mode] || MODE_CFG["remote-sensing"];
  const nav = useNavigate();
  const { user, hasPerm } = useAuth();

  const [projects, setProjects] = useState(null);
  const [cat, setCat] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("list");
  const [suite, setSuite] = useState("all"); // all | hms | standard
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");
  const [folder, setFolder] = useState(null);
  const [owner, setOwner] = useState(null); // super-admin Global Overview: {id, name}
  const [selIds, setSelIds] = useState([]);
  const [trash, setTrash] = useState([]);
  const [trashItems, setTrashItems] = useState([]);
  const [trashBytes, setTrashBytes] = useState(0);
  const [storage, setStorage] = useState({});
  const [trashOpen, setTrashOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivedList, setArchivedList] = useState([]);

  const [newOpen, setNewOpen] = useState(false);
  const [initCommodity, setInitCommodity] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [clientTarget, setClientTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [renFolder, setRenFolder] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);

  const onBulkExport = async () => {
    const ids = selIds.slice(0, 8);
    if (selIds.length > 8) toast.warning("Exporting the first 8 selected projects (max per zip).");
    setExportBusy(true);
    const tid = toast.loading(`Building ${ids.length} Master report(s)…`);
    try {
      const res = await api.post("/projects/bulk-master-report-zip", { ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "SYMIN_Master_Reports.zip";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      const inc = res.headers?.["x-reports-included"];
      toast.success(inc ? `${inc} Master report(s) exported` : "Master reports exported", { id: tid });
    } catch (e) {
      let msg = "Export failed";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ }
      toast.error(msg, { id: tid });
    } finally { setExportBusy(false); }
  };

  const downloadBrief = async (p) => {
    const tid = toast.loading("Preparing Command Brief…");
    try {
      const res = await api.get(`/projects/${p.id}/command-brief.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(p.name || "project").replace(/\s+/g, "_")}_Command_Brief.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Command Brief downloaded", { id: tid });
    } catch (e) { toast.error(apiError(e), { id: tid }); }
  };

  const load = () => api.get("/projects", { params: { mode } }).then((r) => setProjects(r.data)).catch(() => setProjects([]));
  const loadTrash = () => {
    api.get("/trash", { params: { mode } }).then((r) => setTrash(r.data)).catch(() => setTrash([]));
    api.get("/trash/items").then((r) => setTrashItems(r.data)).catch(() => setTrashItems([]));
    api.get("/trash/stats").then((r) => setTrashBytes(r.data.bytes || 0)).catch(() => setTrashBytes(0));
  };
  const loadStorage = () => api.get("/folders/storage").then((r) => setStorage(r.data.folders || {})).catch(() => setStorage({}));
  const loadArchived = () => api.get("/projects", { params: { mode, archived: true } }).then((r) => setArchivedList(r.data)).catch(() => setArchivedList([]));
  useEffect(() => { load(); loadTrash(); loadStorage(); loadArchived(); api.get("/catalog").then((r) => setCat(r.data)).catch(() => {}); }, [mode]);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("new") === "1") {
      setInitCommodity(sp.get("commodity") || null);
      setNewOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  useEffect(() => { setFolder(null); setOwner(null); setTrashOpen(false); setArchivedOpen(false); }, [mode]);
  const refreshAll = () => { load(); loadTrash(); loadStorage(); loadArchived(); };

  const filtered = (projects || []).filter((p) =>
    p.name.toLowerCase().includes(q.toLowerCase()) || (p.country || "").toLowerCase().includes(q.toLowerCase()) ||
    (p.client_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (p.folder_name || "").toLowerCase().includes(q.toLowerCase()));
  const folderKey = (p) => (p.folder_name || p.client_name || "").trim() || "Unassigned";
  const isSuper = ["super_admin", "admin"].includes(user?.role);
  const suiteActive = suite !== "all";
  const suiteCounts = { all: filtered.length, hms: filtered.filter((p) => !!p.hms).length };
  suiteCounts.standard = suiteCounts.all - suiteCounts.hms;
  const suited = suiteActive ? filtered.filter((p) => (suite === "hms" ? !!p.hms : !p.hms)) : filtered;
  const scoped = owner ? suited.filter((p) => (p.created_by_id || "") === owner.id) : suited;
  const _om = new Map();
  if (isSuper) {
    for (const p of suited) {
      const oid = p.created_by_id || "unknown";
      if (!_om.has(oid)) _om.set(oid, { id: oid, name: p.created_by || "Unknown user", items: [] });
      _om.get(oid).items.push(p);
    }
  }
  const owners = [..._om.values()].map((o) => ({
    ...o, folderCount: new Set(o.items.map(folderKey)).size,
  })).sort((a, b) => a.name.localeCompare(b.name));
  const showOwners = isSuper && !q && !owner && !folder && !suiteActive && owners.length > 1;
  const _fm = new Map();
  for (const p of scoped) {
    const k = folderKey(p);
    if (!_fm.has(k)) _fm.set(k, []);
    _fm.get(k).push(p);
  }
  const folders = [..._fm.entries()].sort((x, y) => x[0].localeCompare(y[0])).map(([name, items]) => ({ name, items }));
  const visible = folder ? scoped.filter((p) => folderKey(p) === folder) : scoped;
  const showFolders = !q && !folder && !suiteActive && folders.length > 1;
  const sorted = [...visible].sort((a, b) => {
    const val = (p) => (sortKey === "folder_name" ? folderKey(p) : (p[sortKey] || "").toString());
    const va = val(a).toLowerCase();
    const vb = val(b).toLowerCase();
    return (va < vb ? -1 : va > vb ? 1 : 0) * (sortDir === "asc" ? 1 : -1);
  });

  const toggleSel = (id) => setSelIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const shareFolder = async (name) => {
    try {
      const { data } = await api.post("/clients/portfolio", { client: name });
      const url = `${window.location.origin}${data.path}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(`Portfolio link for "${name}" copied to clipboard`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <button onClick={() => nav("/console")} data-testid="back-to-modes" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-1"><ArrowLeft size={15} /> Back to Console</button>
          <p className="overline">{cfg.overline}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Projects</h1>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto flex-wrap">
          <NotificationBell />
          <button data-testid="archived-btn" title="Archived projects" onClick={() => { setArchivedOpen((v) => !v); setTrashOpen(false); loadArchived(); }}
            className={`relative flex items-center gap-1.5 px-3 h-9 rounded-md border text-xs font-semibold transition-colors ${archivedOpen ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}>
            <Archive size={15} /> Archived
            {archivedList.length > 0 && (
              <span data-testid="archived-count" className="ml-0.5 rounded-full bg-primary/15 text-primary px-1.5 py-0.5 text-[10px] font-bold leading-none">
                {archivedList.length}
              </span>
            )}
          </button>
          <button data-testid="trash-btn" title="Trash" onClick={() => { setTrashOpen((v) => !v); setArchivedOpen(false); }}
            className={`relative flex items-center gap-1.5 px-3 h-9 rounded-md border text-xs font-semibold transition-colors ${trashOpen ? "bg-destructive text-destructive-foreground border-destructive" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}>
            <Trash size={15} /> Trash
            {(trash.length + trashItems.length) > 0 && (
              <span data-testid="trash-count" className="ml-0.5 rounded-full bg-destructive/15 text-destructive px-1.5 py-0.5 text-[10px] font-bold leading-none">
                {trash.length + trashItems.length}
              </span>
            )}
          </button>
          <ProjectsToolbar q={q} onSearch={setQ} sortKey={sortKey} onSortKey={setSortKey}
            sortDir={sortDir} onSortDir={setSortDir} view={view} onView={setView} />
          {hasPerm("create_project") && (
            <NewProjectDialog mode={mode} cfg={cfg} cat={cat} open={newOpen} onOpenChange={setNewOpen} initialCommodity={initCommodity} />
          )}
        </div>
      </div>

      {!archivedOpen && !trashOpen && projects && projects.length > 0 && (
        <div className="flex items-center gap-1 w-fit rounded-lg border border-border bg-card p-1" data-testid="suite-filter">
          {[
            { k: "all", label: "All" },
            { k: "hms", label: "HMS" },
            { k: "standard", label: "Standard" },
          ].map((o) => (
            <button key={o.k} data-testid={`suite-filter-${o.k}`} onClick={() => setSuite(o.k)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 h-8 text-xs font-semibold transition-colors ${
                suite === o.k
                  ? (o.k === "hms" ? "bg-[#0B1420] text-[#FFFFFF]" : "bg-primary text-primary-foreground")
                  : "text-muted-foreground hover:text-foreground"}`}>
              {o.label}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] leading-none tabular-nums ${suite === o.k ? "bg-white/15" : "bg-muted"}`}>{suiteCounts[o.k]}</span>
            </button>
          ))}
        </div>
      )}

      {selIds.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2" data-testid="projects-bulk-bar">
          <span className="text-sm text-muted-foreground">{selIds.length} selected</span>
          <Button size="sm" onClick={onBulkExport} disabled={exportBusy} data-testid="projects-bulk-export">
            <Download size={14} className="mr-1.5" /> {exportBusy ? "Exporting…" : "Export Master PDFs"}
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setBulkOpen(true)} data-testid="projects-bulk-delete">Delete selected</Button>
          <Button variant="ghost" size="sm" onClick={() => setSelIds([])} data-testid="projects-bulk-clear">Clear</Button>
        </div>
      )}

      {(owner || folder) && !trashOpen && (
        <div className="flex items-center gap-3">
          {owner && (
            <button onClick={() => { setOwner(null); setFolder(null); }} data-testid="owner-back"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={15} /> All Users <span className="text-foreground">/ {owner.name}</span>
            </button>
          )}
          {folder && (
            <button onClick={() => setFolder(null)} data-testid="folder-back"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={15} /> All Clients <span className="text-foreground">/ {folder}</span>
            </button>
          )}
        </div>
      )}

      {archivedOpen ? (
        <ArchiveView items={archivedList} onOpen={(p) => nav(cfg.open(p.id))} onChanged={refreshAll} />
      ) : trashOpen ? (
        <TrashView items={trash} trashItems={trashItems} bytes={trashBytes} onChanged={refreshAll} />
      ) : projects === null ? null : showOwners ? (
        <OwnersGrid owners={owners} onOpen={setOwner} />
      ) : showFolders ? (
        <FoldersGrid folders={folders} storage={storage} onOpen={setFolder}
          onRename={setRenFolder} onShare={shareFolder} />
      ) : visible.length === 0 ? (
        <Card className="shadow-none p-12 text-center">
          <h3 className="font-display text-xl font-bold">No projects yet</h3>
          <p className="text-muted-foreground mt-2 text-sm max-w-md mx-auto">{cfg.empty}</p>
          {hasPerm("create_project") && (
            <Button className="mt-5" onClick={() => setNewOpen(true)} data-testid="empty-new-project">
              <Plus size={16} className="mr-1" />Create Project
            </Button>
          )}
        </Card>
      ) : view === "grid" ? (
        <ProjectsGridView projects={sorted} onOpen={(p) => nav(cfg.open(p.id))} onBrief={downloadBrief} />
      ) : (
        <ProjectsTable projects={sorted} cfg={cfg} selIds={selIds}
          onToggleSel={toggleSel}
          onSelectAll={(checked) => setSelIds(checked ? sorted.map((x) => x.id) : [])}
          onOpen={(p) => nav(cfg.open(p.id))}
          onBrief={downloadBrief}
          onEdit={setEditTarget} onMove={setMoveTarget}
          onClientAccount={setClientTarget} onDelete={setDeleteTarget} />
      )}

      <DeleteProjectDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={refreshAll} />
      <BulkDeleteDialog open={bulkOpen} onOpenChange={setBulkOpen} ids={selIds}
        onDeleted={() => { setSelIds([]); refreshAll(); }} />
      <ClientAccountDialog target={clientTarget} onClose={() => setClientTarget(null)} />
      <RenameFolderDialog folder={renFolder} onClose={() => setRenFolder(null)}
        onRenamed={(oldName, newName) => { if (folder === oldName) setFolder(newName); load(); }} />
      <EditSettingsDialog target={editTarget} onClose={() => setEditTarget(null)} onSaved={load} />
      <MoveFolderDialog target={moveTarget} folders={folders} onClose={() => setMoveTarget(null)} onMoved={load} />
    </div>
  );
}
